// oxlint-disable typescript/no-explicit-any
import {
  type StoreApi,
  useStore as useZustandStore,
  create as zustandCreate,
} from 'zustand';
import type { Prettify } from '../../../../lib/typeUtils';

/**
 * Zustand store helpers:
 * - define slices with explicit dependencies (`createSlice`)
 * - compose slices into a typed root store (`composeStore`)
 * - type-level helpers for validating slice dependencies
 */

export type StateUpdater<Root> = (state: Root) => Root;

// Slice creation
// Bivariant function type to allow slices with different Deps to coexist in a tuple.
type SliceCreator<
  Deps extends Record<string, unknown>,
  Slice extends Record<string, unknown>,
> = {
  bivarianceHack<Root extends Deps & Slice>(
    set: (fn: StateUpdater<Root>) => void,
    get: () => Root,
    api: StoreApi<Root>
  ): Slice;
}['bivarianceHack'];

export type SliceDefinition<
  Deps extends Record<string, unknown>,
  Slice extends Record<string, unknown>,
> = {
  create: SliceCreator<Deps, Slice>;
};

export type SliceOf<T> =
  T extends SliceDefinition<any, infer Slice> ? Slice : never;

type SliceDepsOf<T> = T extends SliceDefinition<infer Deps, any> ? Deps : never;

/**
 * Helper for defining a slice with explicit cross-slice dependencies.
 * Usage: `export const mySlice = createSlice<Deps>()<MySlice>((set, get, api) => ({ ... }));`
 */
export function createSlice<
  Deps extends Record<string, unknown> = Record<string, unknown>,
>() {
  return function defineSlice<Slice extends Record<string, unknown>>(
    create: SliceCreator<Deps, Slice>
  ): SliceDefinition<Deps, Slice> {
    return { create };
  };
}

// Store composition
/** Convert a union of object types into an intersection. */
type UnionToIntersection<U> = (
  U extends object ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

type RootStateOf<Slices extends readonly SliceDefinition<any, any>[]> =
  Prettify<UnionToIntersection<SliceOf<Slices[number]>>>;

type FilterSatisfiedDeps<Deps, Root> = Deps extends any
  ? Deps extends Record<string, never>
    ? never
    : Root extends Deps
      ? never
      : Deps
  : never;

type MissingSliceDeps<Slices extends readonly SliceDefinition<any, any>[]> =
  FilterSatisfiedDeps<SliceDepsOf<Slices[number]>, RootStateOf<Slices>>;

// Cool trick that validates on type level all slices have their necessary dependencies installed
export type ValidateSliceDeps<
  Slices extends readonly SliceDefinition<any, any>[],
> =
  MissingSliceDeps<Slices> extends never
    ? unknown
    : [
        'composeStore() error: some slice declares deps that are not provided',
        'Missing keys:',
        MissingSliceDeps<Slices>,
      ];

export const composeStore = <
  const Slices extends readonly SliceDefinition<any, any>[],
>(
  ...slices: Slices & ValidateSliceDeps<Slices>
) => {
  type Root = RootStateOf<Slices>;

  const store = zustandCreate<Root>()((set, get, api) => {
    const setRoot = set as (fn: StateUpdater<Root>) => void;
    const getRoot = get as () => Root;
    const apiRoot = api;

    return slices.reduce<Root>((acc, sliceDef) => {
      const partial = sliceDef.create(setRoot, getRoot, apiRoot);
      return { ...acc, ...(partial as Partial<Root>) };
    }, {} as Root);
  });

  const useStore = <T>(selector: (state: Root) => T): T =>
    useZustandStore(store, selector);

  return { store, useStore };
};
